import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { ITextRich } from 'src/app/api/modules/core/dynamic/components/ITextRich';
import { SafeHtmlPipe } from '../../../../safe-pipes/pipes/safe-html.pipe';

/**
 * The Text Rich Component is used to bind raw HTMLDomStrings to a Component.
 *
 * Content is sanitized through the `safeHtml` pipe before binding.
 *
 * ### CSS Custom Properties
 *
 * Set these from any ancestor to restyle the component — they inherit through
 * view encapsulation, so no `::ng-deep` or `:host-context` is needed.
 *
 * | Property | Description | Default |
 * | --- | --- | --- |
 * | `--ds-body-medium-align` | Text alignment of body content (design-system token) | `center` |
 * | `--ds-text-rich-width` | Host width | `100%` |
 * | `--ds-text-rich-margin` | Host margin | `auto` |
 * | `--ds-text-rich-padding` | Host padding | `0` |
 * | `--ds-text-rich-body-spacer` | Bottom margin of body-medium content | design-system spacer |
 * | `--ds-text-rich-paragraph-spacer` | Bottom margin of each `<p>` in the bound HTML (applied by the global `.drimify-text-rich p` rule) | design-system spacer |
 * | `--ds-text-rich-paragraph-spacer-top` | Top margin of each `<p>` in the bound HTML (same global rule) | browser-native `<p>` margin (`revert`) |
 *
 * The host class `text` is also supported: it left-aligns body content.
 */
@Component({
  selector: 'app-text-rich',
  templateUrl: './text-rich.component.html',
  styleUrls: ['./text-rich.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SafeHtmlPipe],
})
export class TextRichComponent {
  private readonly documentRef = inject(DOCUMENT);

  /**
   * The configuration required for this object to function.
   *
   * Nothing renders until a truthy config arrives.
   */
  readonly config = input<ITextRich | null>();

  /**
   * The resolved view state derived from the config.
   */
  protected readonly templateData = computed(() => {
    const config = this.config();
    if (!config) {
      return undefined;
    }

    const type = config.type || 'none';
    return {
      classType: type === 'none' ? '' : `mat-${type}`,
      data: typeof config.data === 'string' ? config.data : '',
      ariaLabel: config.ariaLabel,
      role: config.role,
    };
  });

  protected onRichTextClick(event: MouseEvent): void {
    this.activateFragmentAnchor(event, false);
  }

  protected onRichTextKeydown(event: KeyboardEvent): void {
    // Only handle Enter and Space keys
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    this.activateFragmentAnchor(event, true);
  }

  /**
   * Event delegation for anchors inside the bound HTML: smooth-scrolls
   * in-page fragment links, leaving all other links to their native behavior.
   *
   * @param focusTarget - also move focus to the scroll target (keyboard
   * activation), so screen reader and tab position follow the jump.
   */
  private activateFragmentAnchor(event: Event, focusTarget: boolean): void {
    const anchor = (event.target as HTMLElement | null)?.closest('a');
    const href = anchor?.getAttribute('href') ?? '';

    // only intercept in-page fragment links
    if (!href.startsWith('#')) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const targetEl = this.documentRef.getElementById(href.slice(1));
    if (!targetEl) {
      return;
    }

    targetEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (focusTarget) {
      // preventScroll: scrollIntoView above already handled scrolling; avoid a second
      // browser-initiated scroll (which also jumps the host page in embedded contexts)
      targetEl.focus({ preventScroll: true });
    }
  }
}
